import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, User, Clock } from "lucide-react";
import { wpApi, WPPost } from "../services/wordpress";

export default function ArticleDetailWordPress() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [article, setArticle] = useState<WPPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchArticle = async () => {
      if (!id) {
        setError("No article ID provided");
        setLoading(false);
        return;
      }

      console.log('ArticleDetail: Loading article with ID/slug:', id);

      try {
        setLoading(true);
        setError(null);

        // Fetch article by slug
        const post = await wpApi.getPostBySlug(id);
        
        if (!post) {
          console.error('ArticleDetail: No post found for slug:', id);
          setError("Article not found");
          setLoading(false);
          return;
        }

        console.log('ArticleDetail: Post loaded:', post.title.rendered);
        setArticle(post);
      } catch (err) {
        console.error("Error fetching article:", err);
        setError(err instanceof Error ? err.message : "Failed to load article");
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [id]);

  // Calculate reading time
  const calculateReadingTime = (content: string) => {
    const wordsPerMinute = 200;
    const wordCount = wpApi.stripHtml(content).split(/\s+/).length;
    const readingTime = Math.ceil(wordCount / wordsPerMinute);
    return `${readingTime} min read`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            {error || "Article not found"}
          </h1>
          <button
            onClick={() => navigate("/market-insights")}
            className="text-blue-600 hover:text-blue-700"
          >
            ← Back to Market Insights
          </button>
        </div>
      </div>
    );
  }

  const featuredImage = wpApi.getFeaturedImageUrl(article, 'large');
  const authorName = wpApi.getAuthorName(article);
  const publishDate = new Date(article.date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  const readingTime = calculateReadingTime(article.content.rendered);

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Header */}
      <header className="bg-gradient-to-b from-gray-50 to-white border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <button
            onClick={() => navigate("/market-insights")}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Market Insights
          </button>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 
              className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6"
              dangerouslySetInnerHTML={{ __html: article.title.rendered }}
            />

            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
              <div className="flex items-center">
                <User className="w-4 h-4 mr-2" />
                <span>{authorName}</span>
              </div>
              <div className="flex items-center">
                <Calendar className="w-4 h-4 mr-2" />
                <span>{publishDate}</span>
              </div>
              <div className="flex items-center">
                <Clock className="w-4 h-4 mr-2" />
                <span>{readingTime}</span>
              </div>
            </div>

            {/* Excerpt */}
            {article.excerpt.rendered && (
              <div 
                className="mt-6 text-lg text-gray-600 italic"
                dangerouslySetInnerHTML={{ __html: article.excerpt.rendered }}
              />
            )}
          </motion.div>
        </div>
      </header>

      {/* Featured Image */}
      {featuredImage && (
        <motion.div
          className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <img
            src={featuredImage}
            alt={wpApi.stripHtml(article.title.rendered)}
            className="w-full rounded-lg shadow-lg"
          />
        </motion.div>
      )}

      {/* Article Content */}
      <motion.article
        className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
      >
        <div 
          className="prose prose-lg max-w-none wordpress-content"
          dangerouslySetInnerHTML={{ __html: article.content.rendered }}
        />
      </motion.article>



      {/* Custom styles for WordPress content - respecting editor styles */}
      <style>{`
        /* Base styles - only apply if no inline styles are present */
        .wordpress-content h1:not([style*="font-size"]) { 
          font-size: 1.875rem; /* 30px - text-3xl */
          font-weight: 700; /* font-bold */
          margin-top: 2rem; /* mt-8 */
          margin-bottom: 1rem; /* mb-4 */
        }
        .wordpress-content h2:not([style*="font-size"]) { 
          font-size: 1.5rem; /* 24px - text-2xl */
          font-weight: 700; /* font-bold */
          margin-top: 1.5rem; /* mt-6 */
          margin-bottom: 0.75rem; /* mb-3 */
        }
        .wordpress-content h3:not([style*="font-size"]) { 
          font-size: 1.25rem; /* 20px - text-xl */
          font-weight: 600; /* font-semibold */
          margin-top: 1rem; /* mt-4 */
          margin-bottom: 0.5rem; /* mb-2 */
        }
        .wordpress-content p { 
          margin-bottom: 1rem; /* mb-4 */
          line-height: 1.625; /* leading-relaxed */
        }
        .wordpress-content ul { 
          list-style-type: disc; /* list-disc */
          list-style-position: inside; /* list-inside */
          margin-bottom: 1rem; /* mb-4 */
        }
        .wordpress-content ul li { 
          margin-bottom: 0.5rem; /* space-y-2 */
        }
        .wordpress-content ol { 
          list-style-type: decimal; /* list-decimal */
          list-style-position: inside; /* list-inside */
          margin-bottom: 1rem; /* mb-4 */
        }
        .wordpress-content ol li { 
          margin-bottom: 0.5rem; /* space-y-2 */
        }
        .wordpress-content li { 
          margin-left: 1rem; /* ml-4 */
        }
        .wordpress-content a { 
          color: #2563eb; /* text-blue-600 */
          text-decoration: underline;
        }
        .wordpress-content a:hover { 
          color: #1d4ed8; /* hover:text-blue-700 */
        }
        .wordpress-content blockquote { 
          border-left-width: 4px; /* border-l-4 */
          border-left-color: #d1d5db; /* border-gray-300 */
          padding-left: 1rem; /* pl-4 */
          font-style: italic; /* italic */
          margin-top: 1rem; /* my-4 */
          margin-bottom: 1rem;
        }
        .wordpress-content img { 
          border-radius: 0.5rem; /* rounded-lg */
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06); /* shadow-md */
          margin-top: 1.5rem; /* my-6 */
          margin-bottom: 1.5rem;
          width: 100%; /* w-full */
        }
        .wordpress-content pre { 
          background-color: #f3f4f6; /* bg-gray-100 */
          border-radius: 0.5rem; /* rounded-lg */
          padding: 1rem; /* p-4 */
          overflow-x: auto; /* overflow-x-auto */
          margin-top: 1rem; /* my-4 */
          margin-bottom: 1rem;
        }
        .wordpress-content code { 
          background-color: #f3f4f6; /* bg-gray-100 */
          padding: 0.125rem 0.25rem; /* px-1 py-0.5 */
          border-radius: 0.125rem; /* rounded */
          font-size: 0.875rem; /* text-sm */
        }
        .wordpress-content table { 
          width: 100%; /* w-full */
          border-collapse: collapse; /* border-collapse */
          margin-top: 1rem; /* my-4 */
          margin-bottom: 1rem;
        }
        .wordpress-content th { 
          border: 1px solid #d1d5db; /* border border-gray-300 */
          padding: 0.5rem 1rem; /* px-4 py-2 */
          background-color: #f3f4f6; /* bg-gray-100 */
          text-align: left; /* text-left */
        }
        .wordpress-content td { 
          border: 1px solid #d1d5db; /* border border-gray-300 */
          padding: 0.5rem 1rem; /* px-4 py-2 */
        }
        
        /* WordPress Block Editor classes */
        .wordpress-content .has-small-font-size { font-size: 0.875rem !important; }
        .wordpress-content .has-normal-font-size { font-size: 1rem !important; }
        .wordpress-content .has-medium-font-size { font-size: 1.125rem !important; }
        .wordpress-content .has-large-font-size { font-size: 1.5rem !important; }
        .wordpress-content .has-x-large-font-size { font-size: 2rem !important; }
        .wordpress-content .has-xx-large-font-size { font-size: 2.5rem !important; }
        .wordpress-content .has-huge-font-size { font-size: 3rem !important; }
        
        /* Respect inline styles from WordPress editor */
        .wordpress-content [style*="font-size"] { /* Inline styles take precedence */ }
        .wordpress-content [style*="color"] { /* Preserve text colors */ }
        .wordpress-content [style*="background"] { /* Preserve backgrounds */ }
        .wordpress-content [style*="text-align"] { /* Preserve alignment */ }
        
        /* WordPress alignment classes */
        .wordpress-content .has-text-align-left { text-align: left; }
        .wordpress-content .has-text-align-center { text-align: center; }
        .wordpress-content .has-text-align-right { text-align: right; }
        .wordpress-content .has-text-align-justify { text-align: justify; }
        
        /* WordPress color classes */
        .wordpress-content .has-text-color { /* Preserve text color */ }
        .wordpress-content .has-background { /* Preserve background color */ }
        
        /* Block spacing */
        .wordpress-content .wp-block-spacer { display: block; }
        .wordpress-content .wp-block-separator { @apply my-8 border-t border-gray-300; }
      `}</style>
    </div>
  );
}